import React, { useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import { Button } from '@theraply/ui';
import { Review } from '../../hooks/useTherapistProfile';

interface ReviewsListProps {
  reviews: Review[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  onLoadMore: () => void;
  isLoadingMore?: boolean;
}

/**
 * Reviews list component with pagination.
 *
 * Features:
 * - Individual review cards
 * - Star rating display
 * - Reviewer name (first + last initial)
 * - Review date formatting
 * - Review text
 * - "Load more" pagination
 *
 * Accessibility:
 * - Semantic HTML structure
 * - ARIA labels for ratings
 * - Keyboard navigation
 *
 * Performance:
 * - Virtualization for large lists (optional)
 * - Lazy loading
 */
export const ReviewsList: React.FC<ReviewsListProps> = ({
  reviews,
  pagination,
  onLoadMore,
  isLoadingMore = false,
}) => {
  const hasMore = pagination.page < pagination.totalPages;

  // Render star rating
  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <StarIcon
          key={i}
          className={`h-5 w-5 ${i < rating ? 'text-yellow-400' : 'text-gray-300'} fill-current`}
        />
      );
    }
    return stars;
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (reviews.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 mb-2">
          <StarIcon className="h-12 w-12 mx-auto" />
        </div>
        <p className="text-gray-600">No reviews yet</p>
        <p className="text-sm text-gray-500 mt-1">Be the first to leave a review!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Reviews */}
      <div className="space-y-4">
        {reviews.map((review) => (
          <div
            key={review.id}
            className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            {/* Header: Rating and Date */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center" aria-label={`Rating: ${review.rating} out of 5 stars`}>
                {renderStars(review.rating)}
              </div>
              <time className="text-sm text-gray-500" dateTime={review.createdAt}>
                {formatDate(review.createdAt)}
              </time>
            </div>

            {/* Review Text */}
            {review.text && (
              <p className="text-gray-700 mb-3 leading-relaxed">{review.text}</p>
            )}

            {/* Reviewer Name */}
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <span className="text-sm font-medium text-indigo-600">
                  {review.reviewerName.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {review.reviewerName}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="text-center pt-4">
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="min-w-[200px]"
          >
            {isLoadingMore ? 'Loading...' : 'Load More Reviews'}
          </Button>
        </div>
      )}

      {/* Pagination Info */}
      <div className="text-center text-sm text-gray-500">
        Showing {reviews.length} of {pagination.total} reviews
      </div>
    </div>
  );
};
